import { useState } from "react";
import * as Scrivito from "scrivito";
import { isExpiredCourse } from "../TrainingCoursePage/_trainingUtils";
import TrainingFilterPanel from "./TrainingFilterPanel";
import TrainingCourseRow from "./TrainingCourseRow";
import { createTranslator } from "../../utils/translate";
import de from "./de.json";
import en from "./en.json";

const translations = { de, en };

const filterAndSortCourses = (
  courses,
  { filterCategories, filterFormats, filterFreeOnly, showExpired }
) =>
  courses
    .filter((course) => {
      if (filterCategories.length > 0) {
        const cats = course.get("category") || [];
        if (!filterCategories.some((k) => cats.includes(k))) return false;
      }
      if (
        filterFormats.length > 0 &&
        !filterFormats.includes(course.get("format"))
      ) {
        return false;
      }
      if (filterFreeOnly && course.get("isFullyBooked")) return false;
      if (!showExpired && isExpiredCourse(course)) return false;
      return true;
    })
    .sort((a, b) => {
      const da = a.get("startDate");
      const db = b.get("startDate");
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });

const TrainingCatalogPage = ({ page }) => {
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterFormats, setFilterFormats] = useState([]);
  const [filterFreeOnly, setFilterFreeOnly] = useState(false);
  const [showExpired, setShowExpired] = useState(false);

  const language = page.language() || "de";
  const t = createTranslator(language, translations);

  const allCourses = (page.children() || []).filter(
    (child) => child.objClass() === "TrainingCoursePage"
  );

  const filtered = filterAndSortCourses(allCourses, {
    filterCategories,
    filterFormats,
    filterFreeOnly,
    showExpired,
  });

  const hasActiveFilters =
    filterCategories.length > 0 ||
    filterFormats.length > 0 ||
    filterFreeOnly ||
    showExpired;

  const resetFilters = () => {
    setFilterCategories([]);
    setFilterFormats([]);
    setFilterFreeOnly(false);
    setShowExpired(false);
  };

  const scrollToTable = () =>
    document
      .getElementById("training-catalog-table")
      ?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-lighter-gray training-catalog-page">
      <div className="page-header-widget bg-blue">
        <div className="container">
          <div className="row">
            <div className="col-12">
              {page.get("shortTitle") ? (
                <Scrivito.ContentTag
                  tag="h1"
                  className="page-header-widget__headline"
                  content={page}
                  attribute="shortTitle"
                />
              ) : (
                <Scrivito.ContentTag
                  tag="h1"
                  className="page-header-widget__headline"
                  content={page}
                  attribute="title"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="page-header_yellow_line" />

      <Scrivito.ContentTag content={page} attribute="body" />

      <div className="container s-py-4">
        <TrainingFilterPanel
          filterCategories={filterCategories}
          setFilterCategories={setFilterCategories}
          filterFormats={filterFormats}
          setFilterFormats={setFilterFormats}
          filterFreeOnly={filterFreeOnly}
          setFilterFreeOnly={setFilterFreeOnly}
          showExpired={showExpired}
          setShowExpired={setShowExpired}
          hasActiveFilters={hasActiveFilters}
          onReset={resetFilters}
          onScrollToTable={scrollToTable}
          language={language}
        />

        <div id="training-catalog-table" className="table-responsive">
          <h3 className="mt-4 mb-2 text-blue fw-bold">
            {t("availableCourses")} ({filtered.length})
          </h3>
          <table className="align-middle training-catalog-table">
            <thead className="bg-lighter-gray p-3">
              <tr>
                <th className="text-gray">{t("date")}</th>
                <th className="text-gray">{t("category")}</th>
                <th className="text-gray">{t("course")}</th>
                <th className="text-gray">{t("format")}</th>
                <th className="text-gray">{t("location")}</th>
                <th className="text-gray">{t("freeSlots")}</th>
                <th className="text-gray text-end">{t("action")}</th>
              </tr>
            </thead>
            <tbody className="bg-white p-3 rounded">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    {t("noCoursesFound")}
                  </td>
                </tr>
              )}
              {filtered.map((course) => (
                <TrainingCourseRow
                  key={course.id()}
                  course={course}
                  language={language}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

Scrivito.provideComponent(
  "TrainingCatalogPage",
  Scrivito.connect(TrainingCatalogPage)
);
